/**
 * Phase 36 — what the experiment will do, shown rather than told (user,
 * 13/10/2026: "this needs to be clearer — animations"): one day's drops as
 * dots, the reserved ones in the experiment LANE's colour exactly where
 * the engine puts them, then the days, the comparison and the choice at
 * the end. One 10 s cycle; reduced motion sees it all at once.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ArrowRight, CalendarRange, Scale, Trophy } from 'lucide-react';
import { EXPERIMENT_METRIC_WORDS, type DropGenExperimentMetric, type DropGenTextMix, laneLabel } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import type { DraftPoolRow } from '../../../../../drop-generator.types';
import { ProvenTag } from '../../../../shared/texts/ProvenTag';
import { gwColor, gwLetter } from '../../cards/strategy-copy';

const DROPS = 8;
const NODE = 'inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-xs animate-dg-story-in motion-reduce:animate-none motion-reduce:opacity-100';
const ARROW = 'h-3.5 w-3.5 shrink-0 text-muted-foreground/60';

/** Mirror of the engine's rule (experiment-share.ts): largest remainder, a half to the experiment, spread evenly. */
export function reservedPositions(count: number, sharePct: number): Set<number> {
  const exact = (count * sharePct) / 100;
  const n = Math.floor(exact) + (exact - Math.floor(exact) >= 0.5 ? 1 : 0);
  const out = new Set<number>();
  for (let k = 0; k < n; k += 1) out.add(Math.floor(((k + 0.5) * count) / n));
  return out;
}

const Node: FC<{ at: number; className?: string; children: ReactNode }> = ({ at, className, children }) => (
  <span className={cn(NODE, className)} style={{ animationDelay: `${at}s` }}>
    {children}
  </span>
);

interface Props {
  pool: DraftPoolRow[];
  /** The lane under test. */
  laneId: string | null;
  sharePct: number;
  days: number;
  metric: DropGenExperimentMetric;
  routeName: (routeId: number) => string;
  /** Phase 37 — the texts the reserved drops send. */
  proven?: DropGenTextMix;
}

export const ExperimentStory: FC<Props> = ({ pool, laneId, sharePct, days, metric, routeName, proven }) => {
  const at = pool.findIndex((r) => r.id === laneId);
  if (at < 0 || pool.length < 2) return null;
  const others = pool.map((_, i) => i).filter((i) => i !== at);
  const reserved = reservedPositions(DROPS, sharePct);
  let turn = 0;
  const letter = (i: number) => (reserved.has(i) ? at : others[turn++ % others.length]);
  const mine = gwLetter(at);
  const rest = others.map(gwLetter).join('/');
  const about = sharePct > 0 ? Math.round(100 / sharePct) : 0;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/20 p-3" data-testid="experiment-story" style={{ '--dg-story': '10s' } as React.CSSProperties}>
      <p className="text-xs text-muted-foreground">
        Every day, <b className="text-foreground">{sharePct} %</b> of the drops{about > 1 ? ` (about 1 in ${about})` : ''} go through lane <b className="text-foreground">{mine} · {laneLabel(pool[at], routeName(pool[at].routeId))}</b>;
        the rest keep the strategy over {rest}. After <b className="text-foreground">{days} day{days === 1 ? '' : 's'}</b> the two sides are compared on the{' '}
        <b className="text-foreground">{EXPERIMENT_METRIC_WORDS[metric]}</b> — then you choose: make {mine} first, take {mine} out, or give {mine} {sharePct} %.
        {proven && proven.proven > 0 ? (
          <>
            {' '}
            Its reserved drops send <ProvenTag mix={proven} what="The experiment's reserved drops" />
          </>
        ) : null}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1" aria-label="one day's drops">
          {Array.from({ length: DROPS }, (_, i) => {
            const g = letter(i);
            return (
              <span
                key={i}
                className={cn('flex h-5 w-5 items-center justify-center rounded font-mono text-[10px] font-bold text-white animate-dg-story-in motion-reduce:animate-none motion-reduce:opacity-100', reserved.has(i) && 'ring-2 ring-offset-1 ring-offset-background')}
                style={{ backgroundColor: gwColor(g), animationDelay: `${0.15 * i}s`, ...(reserved.has(i) ? { ['--tw-ring-color' as string]: gwColor(at) } : {}) }}
                title={reserved.has(i) ? `reserved for ${mine} — the experiment` : `${gwLetter(g)} — the strategy`}
              >
                {gwLetter(g)}
              </span>
            );
          })}
        </span>
        <ArrowRight className={ARROW} />
        <Node at={1.6}>
          <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          the same every day · {days} day{days === 1 ? '' : 's'}
        </Node>
        <ArrowRight className={ARROW} />
        <Node at={2.8}>
          <Scale className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          {mine} vs {rest} on the {EXPERIMENT_METRIC_WORDS[metric]}
        </Node>
        <ArrowRight className={ARROW} />
        <Node at={4} className="border-success/40">
          <Trophy className="h-3.5 w-3.5 text-success" aria-hidden />
          apply the winner — or not
        </Node>
      </div>
    </div>
  );
};
